// ============================================================================
// OBT F3 — İnceleyen çalışma alanı (BM / MY ortak orkestratör)
// ============================================================================
// Görünümler: yayın listesi → cevap listesi → cevap dosyası, ayrıca (BM) park
// önerileri. Kapsam ve yetki backend'de; burada yalnız gerçek verilerle akış
// yönetilir. respondent/öneren adları aktörün alt zincirinden çözülür (tüm
// belediye personeli çekilmez).
//
// role='engineer' (BM): tam işlemler + park önerileri.
// role='deputy'   (MY): BM onayından yükselen cevapları inceler; onaylar,
//                       revizyon ister (BM'ye geri) veya Müdüre gönderir.
//                       Park önerisi kararı YOK (o BM'ye özgü).
// ============================================================================

import { useMemo, useRef, useState } from 'react';
import { useAuth } from '@/auth/AuthContext';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { ObtPageHeader } from '../ObtStates';
import { useReviewerPublications } from './hooks/useReviewerPublications';
import { useChildNames } from './hooks/useChildNames';
import ReviewerPublicationList from './ReviewerPublicationList';
import ResponseReviewPanel from './ResponseReviewPanel';
import ResponseReviewDetail from './ResponseReviewDetail';
import SuggestedParkReviewList from './SuggestedParkReviewList';

/**
 * @param {object} props
 * @param {'engineer'|'deputy'} props.role
 * @param {boolean} [props.enabled]
 * @param {() => void} [props.onBack]
 */
export default function ReviewerWorkspace({ role, enabled = true, onBack }) {
  const { user } = useAuth();
  const personnelId = user?.personnelId || null;
  const isBm = role === 'engineer';

  const pubState = useReviewerPublications({ role, enabled });
  const nameById = useChildNames(personnelId, enabled);

  // view: {kind: 'list'} | {kind: 'panel', pub} | {kind: 'detail', pub, responseId}
  //       | {kind: 'suggestions', pub}
  const [view, setView] = useState({ kind: 'list' });
  const reloadRef = useRef(pubState.reload);
  reloadRef.current = pubState.reload;

  // Güncel yayın objesi (reload sonrası tazelenmiş sayaçlarla) — id ile eşle.
  const currentPub = useMemo(() => {
    if (!view.pub) return null;
    const found = pubState.publications.find(
      (p) => String(p.publication_id) === String(view.pub.publication_id)
    );
    return found || view.pub;
  }, [view.pub, pubState.publications]);

  // Bir cevap aktörün İŞLEM kuyruğunda mı? (buton görünürlüğüyle hizalı.)
  const actionableFor = (r) => {
    const rs = r?.review_state;
    if (isBm) {
      return rs == null || rs === '' || rs === 'bm_review' || rs === 'my_revision_requested';
    }
    return rs === 'bm_approved' || rs === 'my_review' || rs === 'my_approved';
  };

  // Terminal işlemden sonra SIRADAKİ işlem bekleyen cevaba geç (tek tek dönme
  // yerine akış devam etsin); kalmadıysa cevap listesine dön. Sayaçları tazele.
  const goToNextAfter = (doneResponseId) => {
    const list = currentPub?.responses || [];
    const next = list.find(
      (r) =>
        String(r.response_id) !== String(doneResponseId) && actionableFor(r)
    );
    reloadRef.current?.();
    if (next) {
      setView({ kind: 'detail', pub: currentPub, responseId: String(next.response_id) });
    } else {
      setView({ kind: 'panel', pub: currentPub });
    }
  };

  const title = 'Cevap İnceleme';
  const description = isBm
    ? 'Formenlerinizin gönderdiği cevapları inceleyin, revizyon isteyin ve park önerilerini karara bağlayın.'
    : 'Bölge Mühendisi onayından yükselen cevapları inceleyin; onaylayın, revizyon isteyin veya Müdüre gönderin.';

  return (
    <div className="space-y-4">
      <ObtPageHeader
        title={title}
        description={description}
        onBack={view.kind === 'list' ? onBack : null}
      />

      {view.kind === 'list' && (
        <ReviewerPublicationList
          state={pubState}
          onReload={pubState.reload}
          showSuggestions={isBm}
          onOpen={(pub) => setView({ kind: 'panel', pub })}
          onOpenSuggestions={(pub) => setView({ kind: 'suggestions', pub })}
        />
      )}

      {view.kind === 'panel' && currentPub && (
        <ResponseReviewPanel
          publication={currentPub}
          nameById={nameById}
          onBack={() => setView({ kind: 'list' })}
          onOpenResponse={(responseId) =>
            setView({ kind: 'detail', pub: currentPub, responseId })
          }
        />
      )}

      {view.kind === 'detail' && (
        <ResponseReviewDetail
          responseId={view.responseId}
          role={role}
          respondentName={(() => {
            const resp = (currentPub?.responses || []).find(
              (r) => String(r.response_id) === String(view.responseId)
            );
            return resp
              ? nameById.get(String(resp.respondent_personnel_id))
              : undefined;
          })()}
          campaign={{ title: currentPub?.campaign_title, topic: currentPub?.campaign_topic }}
          onChanged={() => reloadRef.current?.()}
          onCompleted={() => goToNextAfter(view.responseId)}
          onBack={() => setView({ kind: 'panel', pub: currentPub })}
        />
      )}

      {view.kind === 'suggestions' && currentPub && (
        <SuggestedParkReviewList
          publication={currentPub}
          nameById={nameById}
          onBack={() => setView({ kind: 'list' })}
          onChanged={() => reloadRef.current?.()}
        />
      )}
    </div>
  );
}
